import {
  ITabelFieldViewProps,
  ITableFields,
  TABLE_FIELD_ALIGN,
  TABLE_FIELD_TYPE,
} from "../../core-component/ui/ICTable";
import { IPagination } from "../../data/AppInterface";
import {
  TReactSetState,
  TSetPaginationFn,
  TUseTranslationTfn,
} from "../../data/AppType";
import { DELETE_EXPENSE, GET_ALL_EXPENSES } from "../../services/Endpoints";
import {
  DEFAULT_STATUS_CODE_SUCCESS,
  MESSAGE_UNKNOWN_ERROR_OCCURRED,
  PAYMENT_METHOD_LABEL,
} from "../../utils/AppConstants";
import { PAYMENT_METHOD } from "../../utils/AppEnumerations";
import {
  handleError,
  sweetAlertError,
  sweetAlertSuccess,
} from "../../utils/AppFunctions";

export interface IExpenses {
  id: string;
  title: string;
  expense_head_label: string;
  expense_date: Date | undefined;
  amount: number;
  payment_method: string;
  transaction_reference: string;
  receipt_media_url: string;
}

export interface IExpensesSearch {
  expenseHead: string;
  paymentMethod: string;
  expenseFromDate: string;
  expenseToDate: string;
  transactionReference: string;
}
export const EXPENSES_SEARCH_INITIAL_VALUE = {
  expenseHead: "",
  paymentMethod: "",
  expenseFromDate: "",
  expenseToDate: "",
  transactionReference: "",
};
export function getExpensesFields<T>(
  actionComponent: (item: T) => JSX.Element,
  t: TUseTranslationTfn
): ITableFields<T>[] {
  return [
    {
      name: t("title"),
      key: "title",
      sort: true,
      type: TABLE_FIELD_TYPE.text,
      align: TABLE_FIELD_ALIGN.left,
      flex: "col-span-2",
    },
    {
      name: t("expense_head"),
      key: "expense_head_label",
      sort: true,
      type: TABLE_FIELD_TYPE.text,
      align: TABLE_FIELD_ALIGN.left,
      flex: "col-span-2",
    },
    {
      name: t("expense_date"),
      key: "expense_date",
      sort: true,
      type: TABLE_FIELD_TYPE.date,
      align: TABLE_FIELD_ALIGN.center,
      flex: "col-span-2",
    },
    {
      name: t("amount"),
      key: "amount",
      sort: true,
      type: TABLE_FIELD_TYPE.text,
      align: TABLE_FIELD_ALIGN.right,
      flex: "col-span-2",
    },
    {
      name: t("payment_method"),
      key: "payment_method",
      sort: false,
      type: TABLE_FIELD_TYPE.format,
      align: TABLE_FIELD_ALIGN.center,
      flex: "col-span-2",
      format: ({ field, data }: ITabelFieldViewProps<T>) => {
        if (Object.values(PAYMENT_METHOD).includes(data["payment_method"])) {
          return PAYMENT_METHOD_LABEL[
            data["payment_method"] as keyof typeof PAYMENT_METHOD_LABEL
          ];
        }
        return "-";
      },
    },

    {
      name: t("trn_ref_no"),
      key: "transaction_reference",
      sort: true,
      type: TABLE_FIELD_TYPE.text,
      align: TABLE_FIELD_ALIGN.left,
      flex: "col-span-2",
      assistiveText: t("transaction_reference_no"),
    },

    {
      name: t("action"),
      key: "action",
      sort: false,
      type: TABLE_FIELD_TYPE.custom,
      align: TABLE_FIELD_ALIGN.center,
      flex: "col-span-1",
      component: actionComponent,
    },
  ];
}

export const getAllExpenses = async (
  setExpensesList: TReactSetState<IExpenses[]>,
  pagination: IPagination,
  setPagination: TSetPaginationFn,
  search: string,
  advanceSearch: IExpensesSearch
) => {
  try {
    const expenses = await GET_ALL_EXPENSES(pagination, search, advanceSearch);
    if (expenses && expenses.code === DEFAULT_STATUS_CODE_SUCCESS) {
      setExpensesList(expenses.data.items);
      setPagination(expenses.data.pagination);
    } else {
      setExpensesList([]);
      sweetAlertError({
        message: expenses.message || MESSAGE_UNKNOWN_ERROR_OCCURRED,
      });
    }
  } catch (error: any) {
    sweetAlertError({
      message: error.message || MESSAGE_UNKNOWN_ERROR_OCCURRED,
    });
    handleError(error, "getAllExpenses");
  }
};

export const deleteExpenses = async (id: string) => {
  try {
    const deleteData = await DELETE_EXPENSE(id);
    if (deleteData && deleteData.code === DEFAULT_STATUS_CODE_SUCCESS) {
      sweetAlertSuccess({ message: deleteData.message });
      return true;
    } else {
      sweetAlertError({
        message: deleteData.message || MESSAGE_UNKNOWN_ERROR_OCCURRED,
      });
      return false;
    }
  } catch (e: any) {
    sweetAlertError({
      message: e.response.message || MESSAGE_UNKNOWN_ERROR_OCCURRED,
    });
    handleError(e, "deleteExpenses");
    return false;
  }
};
